import './VerifyUser.scss';

import { FC, useContext, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppContext } from 'components/AppContext/AppContext';
import { HttpMethod } from 'models/HttpMethod';
import { LogLevel } from 'models/LogLevel';
import { useNavigate } from 'react-router-dom';
import { ApiService } from 'services/ApiService';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface VerifyUserProps { }

const VerifyUser: FC<VerifyUserProps> = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, user, loginWithRedirect, logout } = useAuth0();
  const navigate = useNavigate();
  const { configRepo, setIsProjectSelectable, sendLog } = useContext(AppContext);
  const verifyUrl = 'Users';
  const userService = new ApiService<undefined>(configRepo!.SCHEDULE_API_BASE_URI, verifyUrl);

  useEffect(() => {
    setIsProjectSelectable(false);
  }, []);

  useEffect(() => {
    const refreshUserToken = async () => {
      const token = await getAccessTokenSilently({ cacheMode: 'off' });
      userService.setAccessToken(token);
      return token;
    };

    const setDefaultRoles = async () => {
      const result = await userService.execute('AssignDefaultRoles', HttpMethod.POST);
      if (!result.ok) {
        sendLog('failed to setup default role assignments', LogLevel.error, result);
      }
      await refreshUserToken();
      return result.ok;
    };

    const checkUserHasPendingInvitations = async () => {
      const result = await userService.execute('CheckPendingInvitations', HttpMethod.GET);
      if (result.status === 302) {
        await refreshUserToken();
        return true;
      }
      if (result.status === 304) {
        return false;
      }
      sendLog('failed to CheckPendingInvitations', LogLevel.error, result);
      throw new Error('failed to CheckPendingInvitations');
    };

    const AcceptPendingInvitations = async () => {
      const result = await userService.execute('AcceptPendingInvitations', HttpMethod.POST);
      if (result.ok) {
        await refreshUserToken();
        return true;
      }
      sendLog('failed to AcceptPendingInvitations', LogLevel.error, result);
      throw new Error('failed to AcceptPendingInvitations');
    };

    const verifyUser = async () => {
      sendLog(`starting user verification for ${user?.email}, is authenticated: ${isAuthenticated}, is loading: ${isLoading}`, LogLevel.info);

      try {
        await refreshUserToken();
      } catch (e: any) {
        sendLog(`failed to refresh token for ${user?.email}`, LogLevel.error, e);
        throw e;
      }

      if (isLoading || !isAuthenticated) {
        return Promise.reject();
      }

      const result = await userService.execute('Verify', HttpMethod.GET);
      if (result.ok) {
        return 200;
      }
      sendLog(`failed user verification for ${user?.email} with status ${result.status}`, LogLevel.error, result);
      return result.status;
    };

    const acceptInvitations = async () => {
      if (await AcceptPendingInvitations()) {
        // force reload page to get new token
        window.location.href = `${window.location.origin}/documents`;
      } else {
        navigate('/error?pendingInvitations');
      }
    };

    const welcomeUrl = '/welcome';

    let backOffMs = 500;
    const maxBackoffMs = 4000;

    const parseJwt = (token: string) => {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      // eslint-disable-next-line prefer-template
      const jsonPayload = decodeURIComponent(window.atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));

      return JSON.parse(jsonPayload);
    }

    const navigateToWelcomeTourLater = async () => {
      const encodedToken = await refreshUserToken();

      const decodedToken = parseJwt(encodedToken);

      const buyerId = decodedToken["https://api.blabberjax.com/buyerId"];
      sendLog(`Attempting to navigate to new project for buyer id: ${buyerId}, user: ${user?.email}`, LogLevel.warn);

      if (buyerId) {
        sendLog(`BuyerId found: ${buyerId} navigating to welcome tour for user: ${user?.email}`, LogLevel.info);
        window.location.href = `${window.location.origin}${welcomeUrl}`;
      } else {
        // eslint-disable-next-line no-promise-executor-return
        await new Promise(resolve => setTimeout(resolve, backOffMs));
        backOffMs *= 2;
        if (backOffMs > maxBackoffMs) {
          sendLog(`BuyerId was never found, after ${backOffMs} for user: ${user?.email}`, LogLevel.error);
          // if we never successfully create a default project and company for the user, then force load new project setup:
          window.location.href = `${window.location.origin}/account?new_project=true`;
        } else {
          sendLog(`Attempting to try finding BuyerId again after ${backOffMs} for user: ${user?.email}`, LogLevel.info);
          await navigateToWelcomeTourLater();
        }
      }
    }

    const handleVerificationResult = async (result: number) => {
      sendLog(`starting verification handler for ${user?.email}, result code: ${result}`, LogLevel.info);
      if (result === 403) {
        sendLog(`setting default user roles for ${user?.email}`, LogLevel.info);
        const isSuccessful = await setDefaultRoles();
        if (isSuccessful) {
          sendLog(`successfully set default user roles, checking for pending invitations`, LogLevel.info);
          const hasAcceptedInvitations = await checkUserHasPendingInvitations();
          if (hasAcceptedInvitations) {
            sendLog(`accepting invitations for user: ${user?.email}`, LogLevel.info);
            await acceptInvitations();
          } else {
            sendLog(`navigating to welcome tour for user: ${user?.email}`, LogLevel.info);
            await navigateToWelcomeTourLater();
          }
        } else {
          sendLog(`Failed to set default roles for NEW user: ${user?.email}`, LogLevel.error);
          navigate('/error?roleAssignment');
        }
      } else if (result === 417) {
        sendLog(`navigating to welcome tour for user: ${user?.email}`, LogLevel.error);
        await navigateToWelcomeTourLater();
      } else if (result === 302) {
        await refreshUserToken();
        sendLog(`accepting invitations for user: ${user?.email}`, LogLevel.info);
        await acceptInvitations();
      } else {
        sendLog(`navigating to documents page for user: ${user?.email}`, LogLevel.info);
        navigate('/documents');
      }
    };

    verifyUser()
      .then((r) => handleVerificationResult(r))
      .catch((err) => {
        sendLog(`failed to authenticate user ${JSON.stringify(user)} error: ${err?.message}`, LogLevel.error, err);
        logout();
        loginWithRedirect({
          appState: { targetUrl: '/' },
        });
      });
  }, [isAuthenticated, isLoading]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    navigate('/');
  }

  return (
    <div className="VerifyUser">
      <div className="spinner-border text-primary spinner-inline" role="status" />
      Logging in and setting up your account.  Please be patient...
    </div>);
};

export default VerifyUser;
